<template>
  <section class="why-sogdo-creative">
    <!-- Decorative Shape Images -->
    <div class="shape-decorations">
      <img 
        v-for="(shape, index) in decorativeShapes" 
        :key="index"
        :src="shape.src" 
        :alt="shape.alt" 
        :class="`shape-img shape-${index + 1}`" 
      />
    </div>
    
    <div class="creative-container">
      <div class="why-header">
        <div class="section-badge">
          <span class="badge-text">{{ headerData.badgeText }}</span>
          <img :src="headerData.badgeShape" alt="Badge Shape" class="badge-shape" />
        </div>
        <h1 class="creative-title">
          <span class="title-line-2">
            <span class="text-white me-2">{{ headerData.titlePart1 }}</span>
            <span class="highlight-text">{{ headerData.titlePart2 }}</span>
            <span class="question-mark">{{ headerData.questionMark }}</span>
          </span>
        </h1>
        <p class="creative-subtitle" v-html="headerData.subtitle"></p>
      </div>

      <div class="features-grid">
        <div 
          v-for="(feature, index) in features" 
          :key="index"
          class="feature-card wow fadeInUp" 
          :class="`card-${index + 1}`"
          :data-wow-delay="`${(index + 1) * 0.1}s`"
        >
          <div class="card-background">
            <img :src="feature.cardShape" alt="Card Shape" class="card-shape" />
            <div class="card-pattern"></div>
          </div>
          <div class="card-content">
            <div class="card-icon">
              <i :class="feature.icon"></i>
            </div>
            <h3 class="card-title">{{ feature.title }}</h3>
            <p class="card-description">{{ feature.description }}</p>
            <div class="card-stats">
              <div 
                v-for="(stat, statIndex) in feature.stats" 
                :key="statIndex"
                class="stat"
              >
                <span class="stat-number">{{ stat.number }}</span>
                <span class="stat-label">{{ stat.label }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "WhySogdoItsCreativeComp",
  props: {
    headerData: {
      type: Object,
      required: true,
      default: () => ({
        badgeText: "Why Choose",
        badgeShape: "/shapes/shape-07.png",
        titlePart1: "Why",
        titlePart2: "Sogdo",
        questionMark: "?",
        subtitle: "Experience the difference with <strong>Sogdo</strong> - where innovation meets excellence"
      })
    },
    features: {
      type: Array,
      required: true,
      default: () => []
    },
    decorativeShapes: {
      type: Array,
      required: true,
      default: () => []
    }
  }
};
</script>

<style scoped>
/* Import Google Fonts */
@import url('https://fonts.googleapis.com/css2?family=Archivo:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&family=Open+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,300;1,400;1,500;1,600;1,700;1,800&display=swap');

/* Import Flaticon */
@import '@/assets/scss/Flaticon.scss';

/* --- Why Sogdo Creative Section --- */
.why-sogdo-creative {
  padding: 120px 0;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #0a0e27 0%, #1a1f3a 50%, #2d1b69 100%);
}

/* Shape Decorations */
.shape-decorations {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  pointer-events: none;
}

.shape-img {
  position: absolute;
  opacity: 0.1;
  animation: floatShape 8s ease-in-out infinite;
  filter: brightness(0) saturate(100%) invert(85%) sepia(100%) saturate(1000%) hue-rotate(45deg);
}

.shape-1 {
  top: 5%;
  left: 5%;
  width: 80px;
  height: 80px;
  animation-delay: 0s;
}

.shape-2 {
  top: 15%;
  right: 10%;
  width: 120px;
  height: 120px;
  animation-delay: 1.5s;
}

.shape-3 {
  bottom: 25%;
  left: 8%;
  width: 100px;
  height: 100px;
  animation-delay: 3s;
}

.shape-4 {
  bottom: 15%;
  right: 20%;
  width: 90px;
  height: 90px;
  animation-delay: 4.5s;
}

.shape-5 {
  top: 40%;
  left: 2%;
  width: 110px;
  height: 110px;
  animation-delay: 2s;
}

.shape-6 {
  top: 60%;
  right: 5%;
  width: 70px;
  height: 70px;
  animation-delay: 5.5s;
}

@keyframes floatShape {
  0%, 100% { 
    transform: translateY(0px) rotate(0deg) scale(1); 
    opacity: 0.08;
  }
  50% { 
    transform: translateY(-15px) rotate(90deg) scale(1.05); 
    opacity: 0.12;
  }
}

.creative-container {
  position: relative;
  z-index: 2;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
}

.why-header {
  text-align: center;
  margin-bottom: 80px;
  opacity: 0;
  transform: translateY(30px);
  animation: fadeInDown 1s ease-out 0.5s forwards;
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.section-badge {
  display: inline-block;
  margin-bottom: 20px;
  position: relative;
}

.badge-text {
  background: linear-gradient(135deg, #F6EC4F, #E6D43A);
  color: #0a0e27;
  padding: 8px 24px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 2px;
  font-family: 'Archivo', sans-serif;
  position: relative;
  z-index: 2;
}

.badge-shape {
  position: absolute;
  top: -10px;
  right: -15px;
  width: 40px;
  height: 40px;
  opacity: 0.3;
  animation: rotate 10s linear infinite;
  filter: brightness(0) saturate(100%) invert(85%) sepia(100%) saturate(1000%) hue-rotate(45deg);
}

.creative-title {
  font-size: 72px;
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 30px;
  font-family: 'Archivo', sans-serif;
}

.title-line-2 {
  display: block;
  position: relative;
}

.highlight-text {
  background: linear-gradient(135deg, #F6EC4F, #FB0E76);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  position: relative;
}

.question-mark {
  color: #FB0E76;
  font-size: 0.8em;
  margin-left: 10px;
  animation: bounce 2s infinite;
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-10px); }
  60% { transform: translateY(-5px); }
}

.creative-subtitle {
  font-size: 20px;
  color: #ccc;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-bottom: 80px;
}

.feature-card {
  position: relative;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(246, 236, 79, 0.2);
  border-radius: 20px;
  padding: 40px 30px;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  overflow: hidden;
  backdrop-filter: blur(10px);
  opacity: 0;
  transform: translateY(50px);
  animation: slideInUp 0.8s ease-out forwards;
}

.feature-card:nth-child(1) { animation-delay: 0.1s; }
.feature-card:nth-child(2) { animation-delay: 0.2s; }
.feature-card:nth-child(3) { animation-delay: 0.3s; }
.feature-card:nth-child(4) { animation-delay: 0.4s; }

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(50px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.feature-card:hover {
  transform: translateY(-15px) scale(1.02);
  border-color: #F6EC4F;
  box-shadow: 0 25px 50px rgba(246, 236, 79, 0.2);
}

.card-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.card-shape {
  position: absolute;
  top: -20px;
  right: -20px;
  width: 60px;
  height: 60px;
  opacity: 0.1;
  animation: rotate 20s linear infinite;
  filter: brightness(0) saturate(100%) invert(85%) sepia(100%) saturate(1000%) hue-rotate(45deg);
}

.card-pattern {
  position: absolute;
  top: -50%;
  right: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(246, 236, 79, 0.1) 0%, transparent 70%);
  animation: rotate 20s linear infinite;
}

@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.card-content {
  position: relative;
  z-index: 2;
  text-align: center;
}

.card-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 25px;
  background: linear-gradient(135deg, #F6EC4F, #FB0E76);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  color: #fff;
  transition: all 0.3s ease;
}

.feature-card:hover .card-icon {
  transform: scale(1.05);
}

.card-title {
  font-size: 24px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 15px;
  font-family: 'Archivo', sans-serif;
}

.card-description {
  font-size: 16px;
  color: #ccc;
  line-height: 1.6;
  margin-bottom: 25px;
}

.card-stats {
  display: flex;
  justify-content: space-around;
  gap: 20px;
}

.stat {
  text-align: center;
}

.stat-number {
  display: block;
  font-size: 20px;
  font-weight: 700;
  color: #F6EC4F;
  font-family: 'Archivo', sans-serif;
}

.stat-label {
  display: block;
  font-size: 12px;
  color: #ccc;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-top: 5px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .why-sogdo-creative {
    padding: 80px 0;
  }
  
  .creative-title {
    font-size: 48px;
  }
  
  .features-grid {
    grid-template-columns: 1fr;
    gap: 20px;
    margin-bottom: 60px;
  }
  
  .feature-card {
    padding: 30px 20px;
  }
  
  .card-icon {
    width: 60px;
    height: 60px;
    font-size: 24px;
  }
  
  .card-title {
    font-size: 20px;
  }
  
  .card-description {
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .creative-title {
    font-size: 36px;
  }
  
  .creative-subtitle {
    font-size: 16px;
  }
  
  .badge-text {
    font-size: 12px;
    padding: 6px 16px;
  }
  
  .feature-card {
    padding: 25px 15px;
  }
  
  .card-stats {
    flex-direction: column;
    gap: 10px;
  }
}
</style>
